import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button } from 'react-bootstrap';
import AppContext from 'context/Context';
import OptionListSelect from 'components/dropdowns/OptionListSelect';
import SicFormulaEditor from './SicFormulaEditor';
import { targetTypeKey } from 'helpers/kpiHelper';
import {
  parseFormulaJson,
  resolveValueKind,
  SIC_COMPARE_TYPE,
  SIC_COMPARE_TYPES,
  SIC_INPUT_METHOD,
  SIC_VALUE_KIND
} from 'helpers/sicHelper';
import { post, patch } from 'services/bs-sector-measurable/measurableService';
import {
  swalToastError,
  swalToastSuccess
} from 'components/utilities/SwalToastUtil';

const INPUT_METHOD_OPTIONS = [
  { value: SIC_INPUT_METHOD.manualEnter, label: 'Manual Enter' },
  { value: SIC_INPUT_METHOD.formula, label: 'Formula' }
];

const VALUE_KIND_HINTS = {
  [SIC_VALUE_KIND.date]:
    'Enter the completion date each day. Target is the number of working days allowed (Mon-Fri), e.g. 2 means due within +2 working days.',
  [SIC_VALUE_KIND.time]:
    'Enter a time (hh:mm) each day. Target is a time, e.g. 22:00.',
  [SIC_VALUE_KIND.rag]:
    'Select Green, Amber or Red each day. Target is the lowest acceptable rating, e.g. Amber means Green and Amber are achieved.'
};

const DEFAULT_COMPARE_TYPE_BY_VALUE_KIND = {
  [SIC_VALUE_KIND.yesNo]: SIC_COMPARE_TYPE.equal,
  [SIC_VALUE_KIND.date]: SIC_COMPARE_TYPE.lessOrEqual,
  [SIC_VALUE_KIND.time]: SIC_COMPARE_TYPE.lessOrEqual,
  [SIC_VALUE_KIND.rag]: SIC_COMPARE_TYPE.lessOrEqual
};

const VALUE_KINDS_WITHOUT_UOM = [SIC_VALUE_KIND.yesNo, SIC_VALUE_KIND.rag];

const toFormulaJson = (formulaWithNames, formulaWithIds) =>
  formulaWithIds?.trim()
    ? JSON.stringify({ formulaWithNames, formulaWithIds })
    : null;

const DEFAULT_UOM_BY_VALUE_KIND = {
  [SIC_VALUE_KIND.date]: 'date',
  [SIC_VALUE_KIND.time]: 'hh:mm'
};

const isDefaultUom = uom =>
  Object.values(DEFAULT_UOM_BY_VALUE_KIND).includes(uom.trim());

const uomForSelectedValueKind = (previousUom, selectedValueKind) => {
  if (VALUE_KINDS_WITHOUT_UOM.includes(selectedValueKind)) return '';
  const keepsUserUom = previousUom.trim() !== '' && !isDefaultUom(previousUom);
  if (keepsUserUom) return previousUom;
  return DEFAULT_UOM_BY_VALUE_KIND[selectedValueKind] ?? '';
};

const buildInitialFormData = (measurable, nextOrderNo) => ({
  name: measurable.name ?? '',
  orderNo: measurable.orderNo ?? nextOrderNo,
  targetType: measurable.targetType ?? null,
  uom:
    measurable.uom?.trim() ||
    DEFAULT_UOM_BY_VALUE_KIND[measurable.valueKind] ||
    '',
  numOfDecimal: measurable.numOfDecimal ?? 0,
  compareType: measurable.compareType ?? SIC_COMPARE_TYPE.greaterThan,
  affectsSic: measurable.affectsSic ?? true,
  variationPercentage: measurable.variationPercentage ?? '',
  inputMethod: measurable.inputMethod ?? SIC_INPUT_METHOD.manualEnter,
  formulaJson: measurable.formula ?? null,
  usesTargetFormula: parseFormulaJson(measurable.targetFormula) !== null,
  targetFormulaJson: measurable.targetFormula ?? null
});

export default function SicMeasurableModal({
  measurable,
  sectorId,
  nextOrderNo,
  targetTypeIds,
  formulaFieldOptions,
  handleClose,
  handleSaved
}) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const isEditing = Boolean(measurable.id);
  const [formData, setFormData] = useState(
    buildInitialFormData(measurable, nextOrderNo)
  );
  const [isSaving, setIsSaving] = useState(false);
  const valueKind = resolveValueKind(formData.targetType, targetTypeIds);
  const isYesNoTarget = valueKind === SIC_VALUE_KIND.yesNo;
  const isNumberTarget =
    Boolean(formData.targetType) && valueKind === SIC_VALUE_KIND.number;
  const hasUom = !VALUE_KINDS_WITHOUT_UOM.includes(valueKind);
  const isFormulaInput =
    isNumberTarget && formData.inputMethod === SIC_INPUT_METHOD.formula;
  const isTargetFormulaUsed = isNumberTarget && formData.usesTargetFormula;

  const formulaFieldsWithSameTargetType = useMemo(
    () =>
      formulaFieldOptions.filter(
        field =>
          field.id !== measurable.id &&
          Number(field.targetType) === Number(formData.targetType)
      ),
    [formulaFieldOptions, measurable.id, formData.targetType]
  );

  const updateField = (field, value) =>
    setFormData(previous => ({ ...previous, [field]: value }));

  const handleTargetTypeChange = option => {
    const selectedValueKind = resolveValueKind(option.id, targetTypeIds);
    const isSelectedNumber = selectedValueKind === SIC_VALUE_KIND.number;
    setFormData(previous => ({
      ...previous,
      ...(previous.targetType !== option.id && {
        formulaJson: null,
        targetFormulaJson: null
      }),
      targetType: option.id,
      compareType:
        DEFAULT_COMPARE_TYPE_BY_VALUE_KIND[selectedValueKind] ??
        previous.compareType,
      numOfDecimal: isSelectedNumber ? previous.numOfDecimal : 0,
      uom: uomForSelectedValueKind(previous.uom, selectedValueKind)
    }));
  };

  const resolveInputMethodToSave = () => {
    if (isNumberTarget) return formData.inputMethod;
    return formData.inputMethod === SIC_INPUT_METHOD.formula
      ? SIC_INPUT_METHOD.manualEnter
      : formData.inputMethod;
  };

  const resolveTargetFormulaToSave = () => {
    if (isTargetFormulaUsed) return formData.targetFormulaJson;
    return isEditing ? '' : null;
  };

  const buildRequest = () => ({
    name: formData.name.trim(),
    orderNo: Number(formData.orderNo) || 0,
    targetType: formData.targetType,
    uom: hasUom ? formData.uom.trim() : '',
    numOfDecimal: Number(formData.numOfDecimal) || 0,
    compareType: formData.compareType,
    affectsSic: formData.affectsSic,
    variationPercentage:
      !isNumberTarget || formData.variationPercentage === ''
        ? null
        : Number(formData.variationPercentage),
    inputMethod: resolveInputMethodToSave(),
    formula: isFormulaInput ? formData.formulaJson : null,
    targetFormula: resolveTargetFormulaToSave()
  });

  const findValidationError = () => {
    if (!formData.targetType) return 'Please select a target type.';
    if (isFormulaInput && !parseFormulaJson(formData.formulaJson)) {
      return 'Please build the value formula.';
    }
    if (isTargetFormulaUsed && !parseFormulaJson(formData.targetFormulaJson)) {
      return 'Please build the target formula.';
    }
    return null;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const validationError = findValidationError();
    if (validationError) {
      swalToastError(validationError);
      return;
    }

    setIsSaving(true);
    const response = isEditing
      ? await patch(measurable.id, buildRequest())
      : await post({ ...buildRequest(), sectorId });
    setIsSaving(false);

    if (!response?.success) {
      swalToastError(response.message || 'Failed to save measurable.');
      return;
    }
    swalToastSuccess(isEditing ? 'Measurable updated' : 'Measurable created');
    handleSaved();
  };

  return (
    <Modal
      show
      onHide={handleClose}
      contentClassName="border"
      size={isFormulaInput || isTargetFormulaUsed ? 'lg' : undefined}
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header
          closeButton
          closeVariant={isDark ? 'white' : undefined}
          className="bg-light px-card border-bottom-0"
        >
          <Modal.Title as="h5">
            {isEditing ? 'Edit' : 'Create'} Measurable
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-card">
          <Form.Group className="mb-3">
            <Form.Label className="fs-0">Name</Form.Label>
            <Form.Control
              required
              value={formData.name}
              onChange={e => updateField('name', e.target.value)}
            />
          </Form.Group>

          <div className="d-flex mb-3" style={{ gap: '12px' }}>
            <Form.Group style={{ flex: 2 }}>
              <Form.Label className="fs-0">Target Type</Form.Label>
              <OptionListSelect
                optionKey={targetTypeKey}
                selected={formData.targetType ?? 0}
                changeFn={handleTargetTypeChange}
              />
            </Form.Group>
            {hasUom && (
              <Form.Group style={{ flex: 1 }}>
                <Form.Label className="fs-0">UOM</Form.Label>
                <Form.Control
                  placeholder="qty, hrs, %..."
                  maxLength={20}
                  value={formData.uom}
                  onChange={e => updateField('uom', e.target.value)}
                />
              </Form.Group>
            )}
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="fs-0">Order No.</Form.Label>
              <Form.Control
                type="number"
                value={formData.orderNo}
                onChange={e => updateField('orderNo', e.target.value)}
              />
            </Form.Group>
            {isNumberTarget && (
              <Form.Group style={{ flex: 1 }}>
                <Form.Label className="fs-0">Decimals</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={formData.numOfDecimal}
                  onChange={e => updateField('numOfDecimal', e.target.value)}
                />
              </Form.Group>
            )}
          </div>

          {isYesNoTarget && (
            <div className="mb-3 fs--1 text-muted">
              Target is achieved when the daily answer is{' '}
              <strong>Yes</strong>.
            </div>
          )}

          {VALUE_KIND_HINTS[valueKind] && (
            <div className="mb-3 fs--1 text-muted">
              {VALUE_KIND_HINTS[valueKind]}
            </div>
          )}

          {isNumberTarget && (
            <Form.Group className="mb-3">
              <Form.Label className="fs-0">Daily Value</Form.Label>
              <div className="d-flex" style={{ gap: '14px' }}>
                {INPUT_METHOD_OPTIONS.map(option => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`sic-input-method-${option.value}`}
                    label={option.label}
                    checked={formData.inputMethod === option.value}
                    onChange={() => updateField('inputMethod', option.value)}
                  />
                ))}
              </div>
              {isFormulaInput && (
                <>
                  <div className="fs--1 text-muted mt-2">
                    Value is calculated each day from other measurables, e.g.
                    CCF Production + CCF Stores. Click a badge in the formula
                    to remove it.
                  </div>
                  <SicFormulaEditor
                    key={`value-formula-${formData.targetType}`}
                    formulaWithIds={
                      parseFormulaJson(formData.formulaJson)?.formulaWithIds
                    }
                    fields={formulaFieldsWithSameTargetType}
                    onChange={(formulaWithNames, formulaWithIds) =>
                      updateField(
                        'formulaJson',
                        toFormulaJson(formulaWithNames, formulaWithIds)
                      )
                    }
                  />
                </>
              )}
            </Form.Group>
          )}

          {isNumberTarget && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="sic-target-formula"
                label="Calculate target from formula"
                checked={formData.usesTargetFormula}
                onChange={e =>
                  updateField('usesTargetFormula', e.target.checked)
                }
              />
              {isTargetFormulaUsed && (
                <>
                  <div className="fs--1 text-muted mt-2">
                    Target is calculated each month from other measurables&apos;
                    targets, e.g. Actual Frames * 3.68.
                  </div>
                  <SicFormulaEditor
                    key={`target-formula-${formData.targetType}`}
                    formulaWithIds={
                      parseFormulaJson(formData.targetFormulaJson)
                        ?.formulaWithIds
                    }
                    fields={formulaFieldsWithSameTargetType}
                    onChange={(formulaWithNames, formulaWithIds) =>
                      updateField(
                        'targetFormulaJson',
                        toFormulaJson(formulaWithNames, formulaWithIds)
                      )
                    }
                  />
                </>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="sic-affects-score"
              label="Affects SIC Score"
              checked={formData.affectsSic}
              onChange={e => updateField('affectsSic', e.target.checked)}
            />
          </Form.Group>

          {!isYesNoTarget && (
            <Form.Group className="mb-3">
              <Form.Label className="fs-0">
                Value compare with Target{' '}
                <span className="text-muted">(optional)</span>
              </Form.Label>
              <div className="d-flex flex-wrap mb-2" style={{ gap: '14px' }}>
                <span className="fs--1" style={{ paddingTop: '2px' }}>
                  Target achieved when value is:
                </span>
                {SIC_COMPARE_TYPES.map(option => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`sic-compare-${option.value}`}
                    label={option.label}
                    checked={formData.compareType === option.value}
                    onChange={() => updateField('compareType', option.value)}
                  />
                ))}
              </div>
              {isNumberTarget && (
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '10px' }}
                >
                  <span className="fs--1">Warning band (%)</span>
                  <Form.Control
                    type="number"
                    placeholder="Leave empty for pass / fail only"
                    value={formData.variationPercentage}
                    onChange={e =>
                      updateField('variationPercentage', e.target.value)
                    }
                    style={{ maxWidth: '260px' }}
                  />
                </div>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light px-card border-top-0">
          <Button
            variant="primary"
            type="submit"
            className="px-4 mx-0"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

SicMeasurableModal.propTypes = {
  measurable: PropTypes.object.isRequired,
  sectorId: PropTypes.number.isRequired,
  nextOrderNo: PropTypes.number,
  targetTypeIds: PropTypes.shape({
    yesNo: PropTypes.number,
    date: PropTypes.number,
    time: PropTypes.number,
    rag: PropTypes.number
  }).isRequired,
  formulaFieldOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      targetType: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ).isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSaved: PropTypes.func.isRequired
};
